import { call, put, select } from 'redux-saga/effects';

import * as actions from './meeting-boards.actions';
import * as meetingTasksActions from '../meeting-tasks/meeting-tasks.actions';
import { IMeetingSimple } from '../../../resource/meeting-simple/meeting-simple.types';
import * as meetingBoardsResourceService from '../../../resource/boards/board/board.saga-service';
import * as meetingBoardListsResourceService from '../../../resource/boards/boardList/boardList.saga-service';
import {
  IBoard,
  IBoardCreate,
  IBoardCreateForm,
  IBoardFromNotification,
} from '../../../resource/boards/board/board.types';
import { IBoardItems, IBoardListItems, IMeetingBoards } from './meeting-boards.types';
import { meetingBoardsPageSelectors } from './meeting-boards.selectors';
import * as dialogService from '../../../dialog/dialog.saga-services';
import { boardSelectors } from '../../../resource/boards/board/board.selectors';
import { authSelectors } from '../../../auth/auth.selectors';
import { IUser } from '../../../resource/user/user.types';
import {
  IBoardList,
  IBoardListCreate,
  IBoardListCreateForm,
  IBoardListFromNotification,
} from '../../../resource/boards/boardList/boardList.types';
import { IMeetingTasks, ISimpleTasks } from '../meeting-tasks/meeting-tasks.types';
import { meetingTasksPageSelectors } from '../meeting-tasks/meeting-tasks.selectors';
import { boardListSelectors } from '../../../resource/boards/boardList/boardList.selectors';
import { DropResult } from 'react-beautiful-dnd';

export function* clearMeetingBoards() {
  yield put(actions.clearMeetingBoards());
}

export function* setupMeetingBoards(meetingId: IMeetingSimple['id']) {
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;

  if (!!meetingBoards[meetingId]) {
    return;
  }

  const newMeetingBoards = { ...meetingBoards };
  const boards = (yield call(meetingBoardsResourceService.searchBoards, meetingId)) as IBoard[];

  const boardItems = yield call(setupBoards, boards);

  const boardIds = boards.map((board) => board.id);
  const archivedBoardIds = boards.filter((board) => board.deleted).map((board) => board.id);

  newMeetingBoards[meetingId] = { boardItems, boardIds, archivedBoardIds };

  yield put(actions.storeMeetingBoards(newMeetingBoards));
}

export function setupBoards(boards: IBoard[]) {
  const meetingBoards: IBoardItems = {};

  boards.forEach((board) => {
    if (board.taskTypeIds.length !== 0) {
      board.taskTypeIds.forEach((boardListId) => {
        if (!!meetingBoards[board.id]) {
          meetingBoards[board.id].boardListIds.push(boardListId);
        } else {
          const boardLists: IBoardListItems = { boardListIds: [] };
          boardLists.boardListIds.push(boardListId);
          meetingBoards[board.id] = boardLists;
        }
      });
    } else {
      const boardLists: IBoardListItems = { boardListIds: [] };
      meetingBoards[board.id] = boardLists;
    }
  });
  return meetingBoards;
}

export function* clearMeetingBoardsPageStorage() {
  yield put(actions.clearMeetingBoards());
}

/******************************************BOARDS*************************************** */
/***CREATE BOARD***/
export function* createBoard(boardInfo: IBoardCreateForm) {
  const board = (yield call(meetingBoardsResourceService.createBoard, mapBoardFormToBoardCreate(boardInfo))) as IBoard;

  if (!board) {
    return;
  }

  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;

  const newMeetingBoards = { ...meetingBoards };
  board.meetingIds.forEach((meetingId) => {
    if (!!meetingBoards[meetingId]) {
      const boardLists: IBoardListItems = { boardListIds: [] };
      newMeetingBoards[meetingId].boardItems = { ...newMeetingBoards[meetingId].boardItems, [board.id]: boardLists };
      newMeetingBoards[meetingId].boardIds.push(board.id);
    }
  });
  yield put(actions.storeMeetingBoards(newMeetingBoards));
  yield call(dialogService.closeDialog, 'boardFormDialog');
}

/***UPDATE BOARD***/
export function* updateBoard(boardId: IBoard['id'], boardInfo: IBoardCreateForm) {
  const board = (yield call(meetingBoardsResourceService.updateBoard, {
    id: boardId,
    ...mapBoardFormToBoardCreate(boardInfo),
  })) as IBoard;

  if (!board) {
    return;
  }

  yield call(dialogService.closeDialog, 'boardFormDialog');
}

/***DELETE BOARD***/
export function* deleteBoard(boardId: IBoard['id']) {
  const board = (yield select(boardSelectors.selectResourceById, boardId)) as IBoard;
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;
  const newMeetingBoards = { ...meetingBoards };
  const boardFromResponse = (yield call(meetingBoardsResourceService.deleteBoard, boardId)) as [];
  if (!boardFromResponse) {
    return;
  }

  board.meetingIds.forEach((meetingId: IMeetingSimple['id']) => {
    if (!!meetingBoards[meetingId]) {
      const boardIds = newMeetingBoards[meetingId].boardIds.filter((id) => id !== boardId);
      newMeetingBoards[meetingId] = {
        ...newMeetingBoards[meetingId],
        boardIds,
      };

      if (Object.keys(boardFromResponse).length > 0) {
        newMeetingBoards[meetingId] = {
          ...newMeetingBoards[meetingId],
          boardIds,
          archivedBoardIds: [...newMeetingBoards[meetingId].archivedBoardIds, boardId],
        };
      }
    }
  });
  yield put(actions.storeMeetingBoards(newMeetingBoards));
}

/***SAVE BOARD PUSHER***/
export function* saveBoardSocket(boardResponse: IBoardFromNotification) {
  const user = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (user !== boardResponse.changedBy) {
    const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;

    const newMeetingBoards = { ...meetingBoards };
    let shouldStore = false;
    boardResponse.meetingIds.forEach((meetingId) => {
      if (!!meetingBoards[meetingId]) {
        shouldStore = true;
        if (!meetingBoards[meetingId].boardIds.includes(boardResponse.id)) {
          const boardLists: IBoardListItems = { boardListIds: [] };
          newMeetingBoards[meetingId].boardItems = {
            ...newMeetingBoards[meetingId].boardItems,
            [boardResponse.id]: boardLists,
          };
          newMeetingBoards[meetingId].boardIds.push(boardResponse.id);
        }
      }
    });
    if (shouldStore) {
      yield call(meetingBoardsResourceService.storeBoard, { ...boardResponse });
      yield put(actions.storeMeetingBoards(newMeetingBoards));
    }
  }
}

/***DELETE BOARD PUSHER***/
export function* deleteBoardSocket(boardResponse: IBoardFromNotification) {
  const board = (yield select(boardSelectors.selectResourceById, boardResponse.id)) as IBoard;
  if (!board) {
    return;
  }
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;
  const newMeetingBoards = { ...meetingBoards };

  board.meetingIds.forEach((meetingId: IMeetingSimple['id']) => {
    if (!!meetingBoards[meetingId]) {
      const boardIds = newMeetingBoards[meetingId].boardIds.filter((id) => id !== board.id);
      newMeetingBoards[meetingId] = { ...newMeetingBoards[meetingId], boardIds };
    }
  });
  yield put(actions.storeMeetingBoards(newMeetingBoards));
}

export function* boardDnd(result: DropResult, meetingId: IMeetingSimple['id']) {
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;
  const newMeetingBoards = { ...meetingBoards };

  if (result.destination) {
    const boardId = result.draggableId;
    newMeetingBoards[meetingId].boardIds.splice(result.source.index, 1);
    newMeetingBoards[meetingId].boardIds.splice(result.destination.index, 0, boardId);
  }
  yield put(actions.storeMeetingBoards(newMeetingBoards));
  yield call(meetingBoardsResourceService.saveBoardOrder, { boardIds: newMeetingBoards[meetingId].boardIds });
}

export function* saveBoardOrder(boardIds: IBoard['id'][]) {
  // yield put(actions.storeBoards(boardIds));
  // yield call(meetingBoardsResourceService.saveBoardOrder, { boardIds });
}

/***API MAPPER***/
export const mapBoardFormToBoardCreate = (boardInfo: IBoardCreateForm): IBoardCreate => ({
  name: boardInfo.name,
  meetingIds: boardInfo.meetingIds.map((tupple) => tupple.value),
  color: boardInfo.color,
});

/******************************************BOARDLISTS*************************************** */
/***CREATE BOARDLIST***/

export function* createBoardList(boardListInfo: IBoardListCreateForm) {
  const boardList = (yield call(
    meetingBoardListsResourceService.createBoardList,
    mapBoardListFormToBoardListCreate(boardListInfo),
  )) as IBoardList;

  if (!boardList) {
    return;
  }
  const boardInMeetings: IMeetingSimple['id'][] = [];
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;

  const newMeetingBoards = { ...meetingBoards };
  Object.keys(meetingBoards).forEach((meetingId) => {
    if (newMeetingBoards[meetingId].boardIds.includes(boardList.boardId)) {
      const boardLists = (newMeetingBoards[meetingId].boardItems[Number(boardList.boardId)]
        .boardListIds as unknown) as IBoardList['id'][];

      boardLists.push(boardList.id);

      newMeetingBoards[meetingId].boardItems[
        Number(boardList.boardId)
      ].boardListIds = (boardLists as unknown) as IBoardListItems;

      boardInMeetings.push(meetingId);
    }
  });

  yield put(actions.storeMeetingBoards(newMeetingBoards));

  const meetingTasks = (yield select(meetingTasksPageSelectors.selectMeetingTasks)) as IMeetingTasks;

  const newMeetingTasks = { ...meetingTasks };
  const taskStatuses: ISimpleTasks = { active: [], done: [] };

  boardInMeetings.forEach((meetingId) => {
    newMeetingTasks[meetingId].boardTasks = { ...newMeetingTasks[meetingId].boardTasks, [boardList.id]: taskStatuses };
  });
  yield put(meetingTasksActions.storeMeetingTasks(newMeetingTasks));
  yield call(dialogService.closeDialog, 'boardListFormDialog');
}

/***UPDATE BOARDLIST***/

export function* updateBoardList(boardListId: IBoardList['id'], boardListInfo: IBoardListCreateForm) {
  const boardList = (yield call(meetingBoardListsResourceService.updateBoardList, {
    id: boardListId,
    ...mapBoardListFormToBoardListCreate(boardListInfo),
  })) as IBoardList;

  if (!boardList) {
    return;
  }

  yield call(dialogService.closeDialog, 'boardListFormDialog');
}

/***DELETE BOARDLIST***/

export function* deleteBoardList(boardListId: IBoardList['id']) {
  const boardList = (yield select(boardListSelectors.selectResourceById, boardListId)) as IBoardList;
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;
  const newMeetingBoards = { ...meetingBoards };
  yield call(meetingBoardListsResourceService.deleteBoardList, boardListId);
  const board = (yield select(boardSelectors.selectResourceById, boardList.boardId)) as IBoard;

  board.meetingIds.forEach((meetingId: IMeetingSimple['id']) => {
    if (!!meetingBoards[meetingId]) {
      const boardListIds = (newMeetingBoards[meetingId].boardItems[Number(board.id)]
        .boardListIds as unknown) as IBoardList['id'][];
      const filteredListIds = boardListIds.filter((id) => id !== boardListId);
      newMeetingBoards[meetingId].boardItems[
        Number(boardList.boardId)
      ].boardListIds = (filteredListIds as unknown) as IBoardListItems;
    }
  });
  yield put(actions.storeMeetingBoards(newMeetingBoards));
}

/***SAVE BOARDLIST PUSHER***/
export function* saveBoardListSocket(boardListResponse: IBoardListFromNotification) {
  const user = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (user !== boardListResponse.changedBy) {
    const boardInMeetings: IMeetingSimple['id'][] = [];
    const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;
    let shouldStore = false;
    const newMeetingBoards = { ...meetingBoards };
    Object.keys(meetingBoards).forEach((meetingId) => {
      if (newMeetingBoards[meetingId].boardIds.includes(boardListResponse.boardId)) {
        shouldStore = true;
        const boardLists = (newMeetingBoards[meetingId].boardItems[Number(boardListResponse.boardId)]
          .boardListIds as unknown) as IBoardList['id'][];
        if (!boardLists.includes(boardListResponse.id)) {
          boardLists.push(boardListResponse.id);

          newMeetingBoards[meetingId].boardItems[
            Number(boardListResponse.boardId)
          ].boardListIds = (boardLists as unknown) as IBoardListItems;

          boardInMeetings.push(meetingId);
        }
      }
    });

    const meetingTasks = (yield select(meetingTasksPageSelectors.selectMeetingTasks)) as IMeetingTasks;

    const newMeetingTasks = { ...meetingTasks };
    const taskStatuses: ISimpleTasks = { active: [], done: [] };

    boardInMeetings.forEach((meetingId) => {
      if (!Object.keys(newMeetingTasks[meetingId].boardTasks).includes(boardListResponse.id)) {
        newMeetingTasks[meetingId].boardTasks = {
          ...newMeetingTasks[meetingId].boardTasks,
          [boardListResponse.id]: taskStatuses,
        };
      }
    });

    if (shouldStore) {
      yield call(meetingBoardListsResourceService.storeBoardList, { ...boardListResponse });
      yield put(actions.storeMeetingBoards(newMeetingBoards));
      yield put(meetingTasksActions.storeMeetingTasks(newMeetingTasks));
    }
  }
}

/***DELETE BOARDLIST PUSHER***/

export function* deleteBoardListSocket(boardListResponse: { id: IBoardList['id'] }) {
  const boardList = (yield select(boardListSelectors.selectResourceById, boardListResponse.id)) as IBoardList;
  if (!boardList) {
    return;
  }
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;
  const newMeetingBoards = { ...meetingBoards };
  const board = (yield select(boardSelectors.selectResourceById, boardList.boardId)) as IBoard;

  board.meetingIds.forEach((meetingId: IMeetingSimple['id']) => {
    if (!!meetingBoards[meetingId]) {
      const boardListIds = (newMeetingBoards[meetingId].boardItems[Number(board.id)]
        .boardListIds as unknown) as IBoardList['id'][];
      const filteredListIds = boardListIds.filter((id) => id !== boardList.id);
      newMeetingBoards[meetingId].boardItems[
        Number(boardList.boardId)
      ].boardListIds = (filteredListIds as unknown) as IBoardListItems;
    }
  });
  yield put(actions.storeMeetingBoards(newMeetingBoards));
}

export function* boardListDnd(result: DropResult, meetingId: IMeetingSimple['id'], boardId: IBoard['id']) {
  const meetingBoards = (yield select(meetingBoardsPageSelectors.selectMeetingBoards)) as IMeetingBoards;
  const newMeetingBoards = { ...meetingBoards };
  const boardListIds = (newMeetingBoards[meetingId].boardItems[Number(boardId)]
    .boardListIds as unknown) as IBoardList['id'][];

  if (result.destination) {
    const boardListId = result.draggableId;
    boardListIds.splice(result.source.index, 1);
    boardListIds.splice(result.destination.index, 0, boardListId);
    newMeetingBoards[meetingId].boardItems[Number(boardId)].boardListIds = (boardListIds as unknown) as IBoardListItems;
  }
  yield put(actions.storeMeetingBoards(newMeetingBoards));
  yield call(meetingBoardListsResourceService.saveBoardListOrder, {
    listIds: (newMeetingBoards[meetingId].boardItems[Number(boardId)].boardListIds as unknown) as IBoardList['id'][],
  });
}

export function* saveBoardListOrder(boardIds: IBoard['id'][]) {
  // yield put(actions.storeBoards(boardIds));
  // yield call(meetingBoardsResourceService.saveBoardOrder, { boardIds });
}

/***API MAPPER***/
export const mapBoardListFormToBoardListCreate = (boardListInfo: IBoardListCreateForm): IBoardListCreate => ({
  name: boardListInfo.name,
  boardId: boardListInfo.boardId,
});
